import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import apiClient from "@/api/client";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { DatePicker } from "@/components/ui/DatePicker";
import { useAuthStore } from "@/store/auth";
import { useClients } from "@/features/clients/hooks/useClients";
import { useCompanyChoices } from "@/features/group_finance/hooks/useGroupFinance";
import { useProjectGroups } from "@/features/enterprises/hooks/useProjectGroups";
import { ClientBranchSelector } from "@/features/clients/components/ClientBranchSelector";

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  is_active: boolean;
  is_hidden: boolean;
}

interface CoordinatorPerm {
  assigned: boolean;
  can_view_amounts: boolean;
}

function fullName(u: User) {
  return `${u.first_name} ${u.last_name}`.trim() || u.email;
}

export function ProjectFormPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canViewClients =
    user?.is_ceo || user?.role === "coordinator" ||
    (user?.role === "director" && user?.is_technical_director);
  // Roster + amount grants are director business (set-permissions is technical-director-only
  // on the backend). A flag-based creator (can_create_projects) is auto-assigned as
  // coordinator with amount visibility by the backend, so the section is hidden for them.
  const canManageRoster =
    Boolean(user?.is_ceo) || (user?.role === "director" && Boolean(user?.is_technical_director));

  const [form, setForm] = useState({
    name: "",
    short_name: "",
    client: "",
    company: "",
    group: "",
    description: "",
    is_active: true,
    total_value: "",
    planned_end_date: "",
    client_branch: null as string | null,
    client_sub_branch: null as string | null,
  });

  const { data: clients = [] } = useClients(canViewClients);
  const { data: companies = [] } = useCompanyChoices();
  const { data: projectGroups = [] } = useProjectGroups();
  // First segment of every order number issued for this przedsięwzięcie — the Spółka GK
  // that orders (backend: Order._order_number_company_code), ELEKTIM until one is picked.
  const orderNumberCompanyCode =
    companies.find((c) => c.id === form.company)?.short_code || "ELEKTIM";
  // coordinator id → { assigned, can_view_amounts }
  const [coordPerms, setCoordPerms] = useState<Record<string, CoordinatorPerm>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: () =>
      apiClient.get<{ data: User[] }>("/auth/users/").then((r) => r.data.data),
    enabled: canManageRoster,
  });

  // Only people who can actually take the job: a deactivated account cannot log in, and
  // is_hidden is defined on the model as "nie pojawia sie w listach wyboru". The full
  // catalogue (which keeps both, for Pracownicy/archive views) is the wrong roster here.
  const coordinators = (users ?? []).filter(
    (u) => u.role === "coordinator" && u.is_active && !u.is_hidden,
  );

  const toggleAssigned = (id: string) => {
    setCoordPerms((prev) => {
      const cur = prev[id] ?? { assigned: false, can_view_amounts: false };
      return {
        ...prev,
        [id]: {
          assigned: !cur.assigned,
          can_view_amounts: !cur.assigned ? cur.can_view_amounts : false,
        },
      };
    });
  };

  const toggleAmounts = (id: string) => {
    setCoordPerms((prev) => {
      const cur = prev[id] ?? { assigned: false, can_view_amounts: false };
      return { ...prev, [id]: { ...cur, can_view_amounts: !cur.can_view_amounts } };
    });
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const projectRes = await apiClient.post<{ data: { id: string } }>("/projects/", {
        name: form.name.trim(),
        short_name: form.short_name.trim(),
        client: form.client,
        company: form.company,
        group: form.group || null,
        description: form.description.trim(),
        is_active: form.is_active,
        total_value: form.total_value.trim() || null,
        planned_end_date: form.planned_end_date.trim() || null,
        client_branch: form.client_branch || null,
        client_sub_branch: form.client_sub_branch || null,
        coordinators: Object.entries(coordPerms)
          .filter(([, v]) => v.assigned)
          .map(([id]) => id),
      });
      const projectId = projectRes.data.data.id;

      // Set can_view_amounts permissions for assigned coordinators who have it enabled
      const perms = Object.entries(coordPerms)
        .filter(([, v]) => v.assigned && v.can_view_amounts)
        .map(([userId]) => ({ user: userId, can_view_amounts: true }));
      if (perms.length > 0) {
        await apiClient.post(`/projects/${projectId}/set-permissions/`, perms);
      }
      return projectId;
    },
    onSuccess: (projectId) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      navigate(`/projects/${projectId}`);
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { errors?: Record<string, string[]> } } })
        ?.response?.data?.errors;
      if (data) {
        const parsed: Record<string, string> = {};
        for (const [k, v] of Object.entries(data)) {
          parsed[k] = Array.isArray(v) ? v[0] : String(v);
        }
        setFieldErrors(parsed);
      }
    },
  });

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = "Nazwa jest wymagana";
    if (!form.short_name.trim()) errs.short_name = "Skrót jest wymagany";
    else if (form.short_name.trim().length > 30) errs.short_name = "Skrót może mieć max 30 znaków";
    if (!form.client) errs.client = "Klient jest wymagany";
    if (!form.company) errs.company = "Spółka GK realizująca jest wymagana";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setFieldErrors({});
    createMutation.mutate();
  };

  return (
    <div className="max-w-2xl">
      <Link
        to="/projects"
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 mb-5"
      >
        <ArrowLeft size={14} /> Przedsięwzięcia
      </Link>

      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Nowe przedsięwzięcie</h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
      </div>

      <div className="bg-white dark:bg-gray-800 p-6 flex flex-col gap-5 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Dane podstawowe</h2>

        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Nazwa <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={`block w-full rounded-md border px-3 py-2 text-sm ${fieldErrors.name ? "border-red-400" : "border-gray-300"}`}
            placeholder="np. Rozbudowa sieci ciepłowniczej"
          />
          {fieldErrors.name && <p className="text-xs text-red-600 mt-0.5">{fieldErrors.name}</p>}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Skrót (do numeru zamówienia) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.short_name}
            maxLength={30}
            onChange={(e) => {
              setForm({ ...form, short_name: e.target.value.toUpperCase() });
              setFieldErrors((prev) => ({ ...prev, short_name: "" }));
            }}
            className={`block w-full rounded-md border px-3 py-2 text-sm font-mono ${fieldErrors.short_name ? "border-red-400" : "border-gray-300"}`}
            placeholder="np. RSC"
          />
          {fieldErrors.short_name && (
            <p className="text-xs text-red-600 mt-0.5">{fieldErrors.short_name}</p>
          )}
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Wpisywany w numer zamówienia: {orderNumberCompanyCode}/
            <strong>{form.short_name || "SKR"}</strong>/2026/01/001
          </p>
        </div>

        <div>
          <SearchableSelect
            label="Klient / Inwestor *"
            placeholder="— wybierz klienta —"
            value={form.client}
            onChange={(e) => setForm({ ...form, client: e.target.value, client_branch: null, client_sub_branch: null })}
            options={clients.map((c) => ({ value: c.id, label: c.name + (c.short_name ? ` (${c.short_name})` : "") }))}
            error={fieldErrors.client}
          />
          {clients.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">
              Brak klientów w bazie.{" "}
              <a href="/clients/new" className="underline">Dodaj klienta</a> przed utworzeniem przedsięwzięcia.
            </p>
          )}
        </div>

        <ClientBranchSelector
          clientId={form.client || null}
          selectedBranch={form.client_branch}
          selectedSubBranch={form.client_sub_branch}
          onBranchChange={(id) => setForm((f) => ({ ...f, client_branch: id, client_sub_branch: null }))}
          onSubBranchChange={(id) => setForm((f) => ({ ...f, client_sub_branch: id }))}
          branchError={fieldErrors.client_branch}
          subBranchError={fieldErrors.client_sub_branch}
        />

        <SearchableSelect
          label="Spółka GK realizująca *"
          placeholder="— wybierz spółkę —"
          value={form.company}
          onChange={(e) => {
            setForm({ ...form, company: e.target.value });
            setFieldErrors((prev) => ({ ...prev, company: "" }));
          }}
          options={companies.map((c) => ({ value: c.id, label: `${c.short_code} — ${c.name}` }))}
          error={fieldErrors.company}
        />

        <div>
          <SearchableSelect
            label="Grupa przedsięwzięć"
            placeholder="— bez grupy —"
            value={form.group}
            onChange={(e) => setForm({ ...form, group: e.target.value })}
            options={projectGroups.map((g) => ({
              value: g.id,
              label: g.short_name ? `${g.short_name} — ${g.name}` : g.name,
            }))}
          />
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Jednostka planistyczna. Drobne zlecenia warto łączyć w jedną grupę.
          </p>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Opis</label>
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            placeholder="Opcjonalny opis przedsięwzięcia"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Wartość projektu (PLN)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.total_value}
              onChange={(e) => setForm({ ...form, total_value: e.target.value })}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              placeholder="np. 1500000.00"
            />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Budżet/wartość kontraktu</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Planowana data zakończenia</label>
            <DatePicker
              value={form.planned_end_date}
              onChange={(v) => setForm({ ...form, planned_end_date: v })}
            />
          </div>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            className="rounded"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Aktywne</span>
        </label>
      </div>

      {/* Coordinators + amount permissions combined */}
      {canManageRoster && coordinators.length > 0 && (
        <div className="bg-white dark:bg-gray-800 p-6 mb-6">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Koordynatorzy</h2>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
            Dyrektorzy i księgowi mają zawsze dostęp do kwot. Dla koordynatorów można to określić osobno.
          </p>
          <div className="bg-white dark:bg-gray-800 overflow-x-auto ">
            <table className="w-full text-xs min-w-[400px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 w-8 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Przypisz</th>
                  <th className="text-left px-4 py-2 w-8 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Kwoty</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Koordynator</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {coordinators.map((u) => {
                  const perm = coordPerms[u.id] ?? { assigned: false, can_view_amounts: false };
                  return (
                    <tr key={u.id} className={perm.assigned ? "bg-blue-50 dark:bg-blue-900/30" : ""}>
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={perm.assigned}
                          onChange={() => toggleAssigned(u.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={perm.can_view_amounts}
                          onChange={() => toggleAmounts(u.id)}
                          disabled={!perm.assigned}
                          className="rounded disabled:opacity-30"
                          title={!perm.assigned ? "Najpierw przypisz koordynatora" : ""}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <span className="text-gray-900 dark:text-gray-100">{fullName(u)}</span>
                        <span className="text-gray-400 dark:text-gray-500 ml-2 text-xs">{u.email}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {fieldErrors.detail && (
        <p className="text-sm text-red-600 mb-4">{fieldErrors.detail}</p>
      )}
      {createMutation.isError && !fieldErrors.detail && (
        <p className="text-sm text-red-600 mb-4">Błąd zapisu. Sprawdź dane i spróbuj ponownie.</p>
      )}

      <div className="flex gap-3">
        <Button loading={createMutation.isPending} onClick={handleSubmit}>
          Utwórz przedsięwzięcie
        </Button>
        <Button variant="secondary" onClick={() => navigate("/projects")}>
          Anuluj
        </Button>
      </div>
    </div>
  );
}
